import React, { FC, FunctionComponent, useMemo } from 'react'
import { ThemeProvider } from 'next-themes'

export enum ToastType {
    Danger = 'DANGER',
    Success = 'SUCCESS',
    Warning = 'WARNING'
}

type ModalViewData = {
    email?: string
} & {
    [key: string]: string | number
}

export interface State {
    displaySearch: boolean
    displaySidebar: boolean
    displayDropdown: boolean
    displayModal: boolean
    displayBottomDrawer: boolean
    displayToast: boolean
    displayCartToast: boolean
    isMinicartLoading: boolean
    modalView: string
    modalViewData: ModalViewData | null
    toastText: string | JSX.Element
    toastType: ToastType
    cartToastText: CartToastText
    cartToastType: ToastType
    LoadingIndicator: FunctionComponent
    isFixed?: boolean
    suppressCartQuery?: boolean
}

export interface DispatchActions {
    openSearch: () => void
    closeSearch: () => void
    toggleSearch: () => void
    openSidebar: (suppressCartQuery?: boolean) => void
    closeSidebar: () => void
    toggleSidebar: () => void
    setMinicartLoading: (isLoading: boolean) => void
    closeSidebarIfPresent: () => false | void
    openDropdown: () => void
    closeDropdown: () => void
    openModal: () => void
    closeModal: () => void
    openBottomDrawer: () => void
    closeBottomDrawer: () => void
    setModalView: (view: MODAL_VIEWS, data?: ModalViewData) => void
    openToast: (
        toastText: ToastTextHtml,
        toastType?: ToastType,
        isFixed?: boolean,
        keepOpen?: boolean,
        toastDisplayTimeout?: number
    ) => void
    openCartToast: (
        toastText: CartToastText,
        toastType?: ToastType,
        autoCloseCartToast?: boolean
    ) => void
    closeToast: () => void
    closeCartToast: () => void
}

const initialState = {
    displaySearch: false,
    displaySidebar: false,
    displayDropdown: false,
    displayModal: false,
    displayBottomDrawer: false,
    isMinicartLoading: false,
    modalView: 'LOGIN_VIEW',
    modalViewData: null,
    displayToast: false,
    displayCartToast: false,
    toastText: '',
    toastType: ToastType.Success,
    cartToastText: '',
    cartToastType: ToastType.Success,
    LoadingIndicator: () => null,
    isFixed: false,
    suppressCartQuery: false
}

type Action =
    | {
          type: 'OPEN_SEARCH'
      }
    | {
          type: 'CLOSE_SEARCH'
      }
    | {
          type: 'OPEN_SIDEBAR'
          suppressCartQuery?: boolean
      }
    | {
          type: 'CLOSE_SIDEBAR'
      }
    | {
          type: 'OPEN_TOAST'
          toastText: ToastTextHtml
          toastType: ToastType
          isFixed: boolean
          keepOpen: boolean
      }
    | {
          type: 'CLOSE_TOAST'
      }
    | {
          type: 'OPEN_CART_TOAST'
          toastText: CartToastText
          toastType: ToastType
      }
    | {
          type: 'CLOSE_CART_TOAST'
      }
    | {
          type: 'SET_MINICART_LOADING'
          isLoading: boolean
      }
    | {
          type: 'OPEN_DROPDOWN'
      }
    | {
          type: 'CLOSE_DROPDOWN'
      }
    | {
          type: 'OPEN_MODAL'
      }
    | {
          type: 'CLOSE_MODAL'
      }
    | {
          type: 'OPEN_BOTTOM_DRAWER'
      }
    | {
          type: 'CLOSE_BOTTOM_DRAWER'
      }
    | {
          type: 'SET_MODAL_VIEW'
          view: MODAL_VIEWS
          data?: ModalViewData
      }

type MODAL_VIEWS = 'SIGNUP_VIEW' | 'LOGIN_VIEW' | 'FORGOT_VIEW'
type CartToastText = string | JSX.Element
type ToastTextHtml = string | JSX.Element

export const UIContext = React.createContext<State | any>(initialState)

UIContext.displayName = 'UIContext'

function uiReducer(state: State, action: Action) {
    switch (action.type) {
        case 'OPEN_SEARCH': {
            return {
                ...state,
                displaySearch: true
            }
        }
        case 'CLOSE_SEARCH': {
            return {
                ...state,
                displaySearch: false
            }
        }
        case 'OPEN_SIDEBAR': {
            return {
                ...state,
                displaySidebar: true,
                suppressCartQuery: action.suppressCartQuery
            }
        }
        case 'CLOSE_SIDEBAR': {
            return {
                ...state,
                displaySidebar: false
            }
        }
        case 'OPEN_DROPDOWN': {
            return {
                ...state,
                displayDropdown: true
            }
        }
        case 'CLOSE_DROPDOWN': {
            return {
                ...state,
                displayDropdown: false
            }
        }
        case 'OPEN_MODAL': {
            return {
                ...state,
                displayModal: true,
                displaySidebar: false
            }
        }
        case 'CLOSE_MODAL': {
            return {
                ...state,
                displayModal: false,
                modalViewData: null
            }
        }
        case 'OPEN_BOTTOM_DRAWER': {
            return {
                ...state,
                displayBottomDrawer: true
            }
        }
        case 'CLOSE_BOTTOM_DRAWER': {
            return {
                ...state,
                displayBottomDrawer: false
            }
        }
        case 'OPEN_TOAST': {
            return {
                ...state,
                displayToast: true,
                toastText: action.toastText,
                toastType: action.toastType || ToastType.Success,
                isFixed: action.isFixed,
                keepOpen: action.keepOpen
            }
        }
        case 'CLOSE_TOAST': {
            return {
                ...state,
                displayToast: false
            }
        }
        case 'OPEN_CART_TOAST': {
            return {
                ...state,
                displayCartToast: true,
                cartToastText: action.toastText,
                cartToastType: action.toastType || ToastType.Success
            }
        }
        case 'CLOSE_CART_TOAST': {
            return {
                ...state,
                displayCartToast: false
            }
        }
        case 'SET_MINICART_LOADING': {
            const { isLoading } = action
            return {
                ...state,
                isMinicartLoading: isLoading
            }
        }
        case 'SET_MODAL_VIEW': {
            const { data } = action
            return {
                ...state,
                modalView: action.view,
                modalViewData: data ?? null
            }
        }
    }
}

const DEFAULT_TOAST_TIMEOUT = 5000

export const UIProvider: FC<{
    LoadingIndicator: React.ElementType
    toastTimeout: number
}> = (props) => {
    const [state, dispatch] = React.useReducer(uiReducer, initialState)
    const { toastTimeout } = props
    const openSearch = () => dispatch({ type: 'OPEN_SEARCH' })
    const closeSearch = () => dispatch({ type: 'CLOSE_SEARCH' })
    const toggleSearch = () =>
        state.displaySearch ? closeSearch() : openSearch()

    const openSidebar = (suppressCartQuery = false) =>
        dispatch({ type: 'OPEN_SIDEBAR', suppressCartQuery })
    const closeSidebar = () => dispatch({ type: 'CLOSE_SIDEBAR' })
    const toggleSidebar = () =>
        state.displaySidebar
            ? dispatch({ type: 'CLOSE_SIDEBAR' })
            : dispatch({ type: 'OPEN_SIDEBAR' })
    const closeSidebarIfPresent = () =>
        state.displaySidebar && dispatch({ type: 'CLOSE_SIDEBAR' })

    const openDropdown = () => dispatch({ type: 'OPEN_DROPDOWN' })
    const closeDropdown = () => dispatch({ type: 'CLOSE_DROPDOWN' })

    const openModal = () => dispatch({ type: 'OPEN_MODAL' })
    const closeModal = () => dispatch({ type: 'CLOSE_MODAL' })

    const openBottomDrawer = () => dispatch({ type: 'OPEN_BOTTOM_DRAWER' })
    const closeBottomDrawer = () => dispatch({ type: 'CLOSE_BOTTOM_DRAWER' })

    const openToast = (
        toastText: ToastTextHtml,
        toastType = ToastType.Success,
        isFixed = false,
        keepOpen = false,
        toastDisplayTimeout = toastTimeout
    ) => {
        dispatch({
            type: 'OPEN_TOAST',
            toastText,
            toastType,
            isFixed,
            keepOpen
        })
        if (!keepOpen) setTimeout(closeToast, toastDisplayTimeout)
    }
    const closeToast = () => dispatch({ type: 'CLOSE_TOAST' })

    const openCartToast = (
        toastText: CartToastText,
        toastType = ToastType.Success,
        autoCloseCartToast = true
    ) => {
        dispatch({ type: 'OPEN_CART_TOAST', toastText, toastType })
        if (autoCloseCartToast) setTimeout(closeCartToast, toastTimeout)
    }
    const closeCartToast = () => dispatch({ type: 'CLOSE_CART_TOAST' })

    const setMinicartLoading = (isLoading: boolean) =>
        dispatch({ type: 'SET_MINICART_LOADING', isLoading })

    const setModalView = (view: MODAL_VIEWS, data?: ModalViewData) =>
        dispatch({ type: 'SET_MODAL_VIEW', view, data })

    const value = useMemo(
        () => ({
            ...state,
            openSearch,
            closeSearch,
            toggleSearch,
            openSidebar,
            closeSidebar,
            toggleSidebar,
            closeSidebarIfPresent,
            openDropdown,
            closeDropdown,
            openModal,
            closeModal,
            openBottomDrawer,
            closeBottomDrawer,
            setModalView,
            openToast,
            closeToast,
            openCartToast,
            closeCartToast,
            setMinicartLoading,
            LoadingIndicator: props.LoadingIndicator
        }),
        // eslint-disable-next-line
        [state]
    )

    return <UIContext.Provider value={value} {...props} />
}

export const useUI = (): State & DispatchActions => {
    const context = React.useContext(UIContext)
    if (context === undefined) {
        throw new Error(`useUI must be used within a UIProvider`)
    }
    return context
}

export const ManagedUIContext: FC<{
    LoadingIndicator: React.ElementType
    toastTimeout?: number
}> = ({ children, LoadingIndicator, toastTimeout = DEFAULT_TOAST_TIMEOUT }) => (
    <UIProvider LoadingIndicator={LoadingIndicator} toastTimeout={toastTimeout}>
        <ThemeProvider>{children}</ThemeProvider>
    </UIProvider>
)
